import { useCallback, useContext, useEffect, useState } from "react";

import { UsageHistoryWindow } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  RestrictionName,
  UsageWindowSize,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import { UsageWindow } from "./use-org-ai-credit";
import {
  getOrgRestrictionUsageHistory,
  getUserRestrictionUsageHistory,
} from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

// A single time bucket of the burndown, with per-EntitlementBucket usage
// (keyed by the bucket enum name, e.g. "ENTITLEMENT_BUCKET_OVERAGE").
export interface BurndownWindow {
  start: Date;
  end: Date;
  total: number;
  byBucket: Record<string, number>;
}

// Range-wide usage for the queried period. These come from the response's
// top-level total_usage / usage_by_bucket, which the server derives from the
// grant burndown (the authoritative split) — not a re-sum of the per-window
// series. That matters because OpenMeter drops windows for sub-window-aligned
// ranges, so the per-window bars can be empty while this summary is correct.
export interface UsageSummary {
  total: number;
  byBucket: Record<string, number>;
}

const EMPTY_SUMMARY: UsageSummary = { total: 0, byBucket: {} };

// uint64 fields arrive as bigint over connect-web; project to number for the chart.
const mapHistoryWindows = (windows: UsageHistoryWindow[]): BurndownWindow[] =>
  windows.map((w) => ({
    start: w.periodStart?.toDate() ?? new Date(0),
    end: w.periodEnd?.toDate() ?? new Date(0),
    total: Number(w.totalUsage),
    byBucket: Object.fromEntries(
      Object.entries(w.usageByBucket).map(([k, v]) => [k, Number(v)]),
    ),
  }));

const mapSummary = (r: {
  totalUsage: bigint;
  usageByBucket: Record<string, bigint>;
}): UsageSummary => ({
  total: Number(r.totalUsage),
  byBucket: Object.fromEntries(
    Object.entries(r.usageByBucket).map(([k, v]) => [k, Number(v)]),
  ),
});

// coalesceToDaily merges hour-resolution windows into per-UTC-day buckets. We
// always query OpenMeter at HOUR resolution: a DAY-resolution query drops the
// first day whose window starts before the entitlement anchor
// (MeasureUsageFrom), silently losing that day's usage. Fetching hourly and
// summing into UTC days here gives an accurate daily view with no cut-off.
export const coalesceToDaily = (
  windows: BurndownWindow[],
): BurndownWindow[] => {
  const byDay = new Map<number, BurndownWindow>();
  for (const w of windows) {
    const dayStart = new Date(
      Date.UTC(
        w.start.getUTCFullYear(),
        w.start.getUTCMonth(),
        w.start.getUTCDate(),
      ),
    );
    const key = dayStart.getTime();
    let day = byDay.get(key);
    if (!day) {
      const dayEnd = new Date(dayStart);
      dayEnd.setUTCDate(dayEnd.getUTCDate() + 1);
      day = { start: dayStart, end: dayEnd, total: 0, byBucket: {} };
      byDay.set(key, day);
    }
    day.total += w.total;
    for (const [bucket, value] of Object.entries(w.byBucket)) {
      day.byBucket[bucket] = (day.byBucket[bucket] ?? 0) + value;
    }
  }
  return Array.from(byDay.values()).sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
};

export interface OrgAIBurndown {
  isLoading: boolean;
  windows: BurndownWindow[];
  summary: UsageSummary;
  refresh: () => void;
}

// Fetches the org-level AI credit burndown for a window, always at HOUR
// resolution (callers coalesce to daily for display via coalesceToDaily). The
// org holds only the paid entitlement, so the returned buckets are the pack
// types (welcome / support / purchased / overage).
export const useOrgAIBurndown = (
  roleId: string,
  regionCode: string,
  usageWindow: UsageWindow,
  enabled: boolean,
): OrgAIBurndown => {
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(enabled);
  const [windows, setWindows] = useState<BurndownWindow[]>([]);
  const [summary, setSummary] = useState<UsageSummary>(EMPTY_SUMMARY);

  const { from, to } = usageWindow;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOrgRestrictionUsageHistory(
        {
          organizationId: toUUID(roleId),
          restrictionName: RestrictionName.AI_CREDIT_USAGE,
          windowSize: UsageWindowSize.HOUR,
          fromTime: from,
          toTime: to,
        },
        callOptionsHeaders(regionCode, roleId),
      );
      setWindows(mapHistoryWindows(response.windows));
      setSummary(mapSummary(response));
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [roleId, regionCode, from, to, showError]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  return { isLoading, windows, summary, refresh: load };
};

// Fetches a single user's AI credit burndown for a window, always at HOUR
// resolution (callers coalesce to daily for display via coalesceToDaily).
// Buckets are Free vs Paid (overage). Disabled until a user is selected
// (userId empty).
export const useUserAIBurndown = (
  userId: string,
  regionCode: string,
  roleId: string,
  usageWindow: UsageWindow,
  enabled: boolean,
): OrgAIBurndown => {
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(false);
  const [windows, setWindows] = useState<BurndownWindow[]>([]);
  const [summary, setSummary] = useState<UsageSummary>(EMPTY_SUMMARY);

  const { from, to } = usageWindow;

  const load = useCallback(async () => {
    if (!userId) {
      setWindows([]);
      setSummary(EMPTY_SUMMARY);
      return;
    }
    setIsLoading(true);
    try {
      const response = await getUserRestrictionUsageHistory(
        {
          userId: toUUID(userId),
          restrictionName: RestrictionName.AI_CREDIT_USAGE,
          windowSize: UsageWindowSize.HOUR,
          fromTime: from,
          toTime: to,
        },
        callOptionsHeaders(regionCode, roleId),
      );
      setWindows(mapHistoryWindows(response.windows));
      setSummary(mapSummary(response));
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [userId, regionCode, roleId, from, to, showError]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  return { isLoading, windows, summary, refresh: load };
};
